import { Box, GridLegacy as Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { InputText } from '@/components/shared/InputText';

import { useTranslation } from 'react-i18next';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { useAppDispatch } from '@/lib/hooks';
import { CompanyDanskeBankSettings } from '@/models/payment';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';
interface IntectConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
    danskeBankButtonTitle: string | null;
}

export const DanskeBankConnect = (props: IntectConnectProps) => {
    const { t } = useTranslation();
    const [danskeBankData, setDanskeBankData] =
        useState<CompanyDanskeBankSettings>({} as any);
    const [companyNameError, setCompanyNameError] = useState<boolean>(false);
    const [_ibanError, setIbanError] = useState<boolean>(false);
    const [_cvrError, setCvrError] = useState<boolean>(false);
    const { setLoading } = useLoader();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const _dispatch = useAppDispatch();

    const fetchCompany = useCallback(() => {
        setLoading(true);
        PaymentsAPIServiceV2.getInstance()
            .getCompanyDanskeBankSettings()
            .then((response: any) => {
                const settings = response.data as CompanyDanskeBankSettings;
                setDanskeBankData(settings);
            })
            .catch(backendErrorHandler())
            .finally(() => {
                setLoading(false);
            });
    }, [setLoading, backendErrorHandler]);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    const isValidDanskeBank = (data: CompanyDanskeBankSettings) => {
        if (!data.name) {
            setCompanyNameError(true);
            return false;
        } else {
            setCompanyNameError(false);
        }

        if (!data.cvr) {
            setCvrError(true);
            return false;
        } else {
            setCvrError(false);
        }

        if (!data.iban) {
            setIbanError(true);
            return false;
        } else {
            setIbanError(false);
        }

        return true;
    };

    const onSave = () => {
        if (isValidDanskeBank(danskeBankData)) setLoading(true);
        PaymentsAPIServiceV2.getInstance()
            .updateCompanyDanskeBankSettings(danskeBankData)
            .then(() => {
                setAlert(
                    'Danske bank settings updated successfully',
                    'success'
                );
                props.onSaveSuccess();
            })
            .catch(backendErrorHandler('Could not save settings'))
            .finally(() => setLoading(false));
    };

    return (
        <>
            <Box width={'100%'} margin={'20px 0 10px'}>
                <InputText
                    value={danskeBankData?.name}
                    label={t('name')}
                    error={companyNameError}
                    onChange={(val) => {
                        setDanskeBankData({
                            ...danskeBankData,
                            name: val.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    value={danskeBankData.iban}
                    label={t('iban')}
                    onChange={(val) => {
                        setDanskeBankData({
                            ...danskeBankData,
                            iban: val.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    value={danskeBankData.cvr}
                    label={t('cvr')}
                    onChange={(val) => {
                        setDanskeBankData({
                            ...danskeBankData,
                            cvr: val.target.value
                        });
                    }}
                />
            </Box>

            <Grid m={'20px 0'} container justifyContent={'space-around'}>
                <SecondaryButtonStyle
                    onClick={props.onClose}
                    sx={{ width: '170px' }}
                >
                    {t('back')}
                </SecondaryButtonStyle>

                <PrimaryButtonStyle
                    onClick={onSave}
                    sx={{ width: '170px' }}
                    disabled={
                        !danskeBankData?.name ||
                        !danskeBankData?.iban ||
                        !danskeBankData?.cvr
                    }
                >
                    {props?.danskeBankButtonTitle}
                </PrimaryButtonStyle>
            </Grid>
        </>
    );
};
